import {
    MouseEvent, useEffect, useMemo, useRef, useState,
} from 'react';
import { Calendar } from 'data/firestore/agent/calendar/types';
import { Calendarevent } from 'data/firestore/agent/calendarevent/types';
import getDisplayName from 'data/services/profile/getDisplayName';
import { CalendarEventClick } from 'types';
import getUnavailableSlots from './helpers/getUnavailableSlots';
import getHeightOfMinutes from '../helpers/getHeightOfMinutes';
import { CalendarItemContainer, UnavailableTime } from './styled';
import Event from './event';
import getEventPositionInSlot from './helpers/getEventPositionInSlot';
import getOverlappingEventsLayout from './helpers/getOverlappingEventsLayout';

interface CalendarItemProps {
    id?: string;
    color: string;
    getEventColor?: (event: Calendarevent) => string;
    availability?: Calendar['availability'];
    events?: Calendarevent[];
    timeZone: string;
    selectedEvent?: string | null;
    isCollapsed?: boolean;
    calendarNames?: Record<string, string>;
    onEventClick?: CalendarEventClick;
    onAvailableSpaceClick: (e: MouseEvent<HTMLDivElement>) => void;
}

const CalendarItem = ({
    id,
    color,
    getEventColor = () => color,
    events = [],
    selectedEvent,
    availability = [],
    timeZone,
    isCollapsed = false,
    calendarNames,
    onEventClick = () => {},
    onAvailableSpaceClick = () => {},
}: CalendarItemProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);

    const unAvailableSlots = useMemo(
        () => getUnavailableSlots(
            availability.map(({ startAt, duration }) => ({ startAt, duration })),
        ).map(({ startAt, duration }) => (
            <UnavailableTime
                id={`day-view-unavailable-time-slot__${startAt}`}
                key={`day-view-unavailable-time-slot__${startAt}`}
                height={getHeightOfMinutes(duration / 60) - 1}
                top={getHeightOfMinutes(startAt / 60) + 1}
                onClick={(e) => e.stopPropagation()}
            />
        )),
        [availability],
    );

    const eventsLayout = useMemo(() => getOverlappingEventsLayout(events), [events]);

    const eventsList = useMemo(
        () => events.map(
            (event, index) => {
                const description = event.people ? event.title : '';
                const { column, totalColumns } = eventsLayout[index];

                const positionInSlot = getEventPositionInSlot({
                    maxSize: containerWidth,
                    totalEventsInSameSlot: totalColumns,
                    totalPrevEventsInSameSlot: column,
                });

                return (
                    <Event
                        key={`day-view-event__${event.id}`}
                        title={event.people ? getDisplayName(event.people) : event.title}
                        description={description}
                        color={getEventColor(event)}
                        status={event.status}
                        calendarName={calendarNames?.[event.calendarId]}
                        startAt={event.startAt}
                        duration={event.duration}
                        timeZone={timeZone}
                        isSelected={event.id === selectedEvent}
                        isPending={event.status?.name === 'pending-approval' && event.status.removable === false}
                        isRejected={event.status?.name === 'rejected' && event.status.removable === false}
                        positionInSlot={positionInSlot}
                        onEventClick={
                            ({ triggerRef }) => onEventClick({
                                triggerRef,
                                event: event as unknown as Record<string, unknown>,
                            })
                        }
                    />
                );
            },
        ),
        [
            timeZone,
            color,
            getEventColor,
            events,
            eventsLayout,
            calendarNames,
            onEventClick,
            selectedEvent,
            containerWidth,
        ],
    );

    useEffect(
        () => {
            if (containerRef.current?.clientWidth) {
                setContainerWidth(containerRef.current?.clientWidth);
            }
        },
        [containerRef.current?.clientWidth],
    );

    return (
        <CalendarItemContainer
            id={id}
            ref={containerRef}
            $collapsed={isCollapsed}
            onClick={onAvailableSpaceClick}
        >
            {unAvailableSlots}
            {eventsList}
        </CalendarItemContainer>
    );
};

export default CalendarItem;
